import type { RefObject } from "react";
import type { HostAdapter, HttpMethod } from "@cinesys/experience-ui/host";
import CustomerApiService, { ApiError } from "../CustomerApiService";
import { FetchError } from "../features/item-browser/shared/ApiService";
import ColorProvider from "../features/item-browser/shared/components/ColorProvider";
import { customActionContextForMenuType } from "../features/item-gear/menuTypeContext";
import { useCustomActionOptions } from "../features/item-gear/service";
import { pluginEnabled } from "../features/item-browser/shared/utils";
import { navigateFromHost } from "./NavigateBridge";
import { pushToast } from "./Toast";

/**
 * customer-apps' implementation of the shared `HostAdapter` seam (`cinesys-ui`'s `packages/experience-ui/src/
 * host/HostAdapter.ts`) — wired up once in `index.tsx` via `setHostAdapter`, before the app
 * mounts. `notify`/`getOrCreateModalRoot`/`getVisibleSearchChips` are what the lifted
 * `CollectionBrowser` tree's `growl`/`getModal`/`getSearchItems`/`navigateToCollection`
 * (`cinesys-ui`'s `packages/experience-ui/src/components/CollectionBrowser/platform.ts`) actually call now —
 * see `HostAdapter.ts`'s own per-member doc comments for the call-site accounting.
 * `navigateToSearch` is now wired to the lifted `MetadataViewer` tree's `openSearch`
 * (`cinesys-ui`'s `packages/experience-ui/src/components/MetadataViewer/platform.ts`) — see this file's
 * `navigateToSearch` doc comment.
 */

const modalRoots: Record<string, HTMLElement> = {};

// The lifted `CollectionBrowser` tree's call sites keep Cantemo's own literal `/cs_api/...` route
// paths verbatim, same convention `item-browser/shared/ApiService.ts`'s CS_API_PREFIX strip
// used to establish directly — this app mounts the same route segments without that prefix, so
// stripping it here means no call site needs editing.
const CS_API_PREFIX = "/cs_api";

/**
 * This app's answer for `HostAdapter.useGearMenuOptions` — reached via `useHostGearMenuOptions`
 * by every lifted component that renders a `<Gear>` (`ItemTitle.tsx`/`ResultCard.tsx`, and
 * `ItemBrowserList.tsx`'s bulk-selection gear as of Monday S2.5d) since none of them can import
 * `useCustomActionOptions`/`customActionContextForMenuType` directly from the package. Declared
 * as a named function rather than inlined into the object literal below so
 * `react-hooks/rules-of-hooks` sees an unambiguously `use`-prefixed custom hook.
 *
 * `customActionContextForMenuType` returns `undefined` for every `menuType` with no iconik context
 * (most of them), and `useCustomActionOptions` treats that as a disabled query — so an unmapped
 * menu type costs nothing, exactly as it does at today's call sites.
 */
function useGearMenuOptions(menuType: string | undefined) {
  return useCustomActionOptions(customActionContextForMenuType(menuType));
}

export const customerAppsHostAdapter: HostAdapter = {
  api: {
    // `ApiError` -> `FetchError` translation (closes S2.1's deferred item, Monday S2.5a) —
    // `fileActions.ts` is the real, confirmed `isFetchError` duck-typing consumer S2.1's own
    // decisions.md entry named as the eventual trigger for this. `FetchError` is item-browser's
    // own shape (`shared/ApiService.ts`), imported here rather than redefined, since the lifted
    // `CollectionBrowser`/`MetadataViewer` trees' own `ApiService` forwarders have no translation
    // of their own to duplicate this into.
    fetch<T, B = unknown>(
      method: HttpMethod,
      path: string,
      body?: B
    ): Promise<T> {
      const adaptedPath = path.startsWith(CS_API_PREFIX)
        ? path.slice(CS_API_PREFIX.length)
        : path;
      return CustomerApiService.fetch<T, B>(method, adaptedPath, body).catch(
        (e) => {
          if (e instanceof ApiError) {
            // Set `.message` directly rather than relying on the constructor's
            // `response?.message || 'Unknown error'` fallback, which would resurrect a deliberate
            // empty message (a non-JSON 502/504 body).
            const response: Record<string, string> = { message: e.message };
            if (e.code) {
              response.code = e.code;
            }
            const error = new FetchError(e.status, response);
            error.message = e.message;
            // "ApiError" is just the class's own default when nothing set a real name -- match
            // Cantemo's 'Error' fallback instead of leaking our class name.
            error.name = e.name === "ApiError" ? "Error" : e.name;
            throw error;
          }
          throw e;
        }
      );
    },

    // Same CS_API_PREFIX strip as `fetch` above, then resolved against `CustomerApiService
    // .apiUrl` instead of returned relative — a plain `<a href>` resolves a relative path against
    // the PAGE's own origin, not customer-api's. Backs `JobViewer/components/JobResponse.tsx`'s
    // lifted call site; unreached in practice today (`pluginEnabled('cs_job_viewer')` is
    // hard-false, so no backend ever populates `job.response.links`), same honest-degrade posture
    // as `getVisibleSearchChips`.
    resolveUrl: (path) => {
      const adaptedPath = path.startsWith(CS_API_PREFIX)
        ? path.slice(CS_API_PREFIX.length)
        : path;
      return CustomerApiService.apiUrl + adaptedPath;
    },
  },

  // Cantemo's `$.growl` is a jQuery toast bound to Portal's own page chrome — renders through
  // `./Toast.tsx` here instead. Still logs too, so this stays useful from a headless test/console
  // even with no `<ToastHost>` mounted.
  notify: (message, kind) => {
    if (kind === "error") {
      console.error(`[host] ${message}`);
    } else {
      console.info(`[host] ${message}`);
    }
    pushToast(message, kind);
  },

  // Same find-or-create-and-cache contract as Cantemo's own `getModal`: a `.react_<key>_modal`
  // div under `document.body`, ref cached on `window.cs_react_modals` because
  // `CollectionBrowser.tsx`'s unmodified code reads that exact global directly.
  getOrCreateModalRoot: <T extends object>(key: string) => {
    if (!modalRoots[key]) {
      const root = document.createElement("div");
      root.className = `react_${key}_modal`;
      document.body.appendChild(root);
      modalRoots[key] = root;
    }
    window.cs_react_modals = window.cs_react_modals ?? {};
    if (!window.cs_react_modals[key]) {
      window.cs_react_modals[key] = { current: null };
    }
    return {
      modalRef: window.cs_react_modals[key] as unknown as RefObject<T>,
      modalRoot: modalRoots[key],
    };
  },

  // Cantemo writes a Vidispine-syntax query into Portal's own search page via a
  // cookie/localStorage handoff. No such deep link exists here yet — this only opens the
  // item-browser route. `samePage` routes through `navigateFromHost` rather than
  // `window.open(path, "_self")` — the latter is a full document reload for what's actually an
  // in-app react-router route. Real caller now: `MetadataViewer/platform.ts`'s `openSearch`, from
  // `MetadataViewerValue.tsx`'s "search for this value" links.
  navigateToSearch: (_query, options) => {
    if (options?.samePage) {
      navigateFromHost("/item-browser");
    } else {
      window.open("/item-browser", "_blank");
    }
  },

  // See `NavigateBridge.tsx` — `useNavigate()` only works inside a component, so this reads back
  // the live navigate function `<NavigateBridge>` stashes there once, at the router root.
  navigate: (path, options) => navigateFromHost(path, options),

  // No live jQuery search-results DOM to scrape in this app — always empty, the same honest
  // answer `cs_collection_browser/platform.ts`'s `getSearchItems` already gives.
  getVisibleSearchChips: () => [],

  // Cantemo's version reads `window.location.hash` (see `HostAdapter.ts`'s own doc comment) —
  // this app is a plain path-routed SPA (Monday 12965030446, `docs/decisions.md`'s 2026-09-03
  // entry), so this reads `window.location.pathname` instead. A saved search is stored as a
  // collection under the hood (`CollectionBrowserTree.tsx` highlights the active row the same way
  // for both), so both `/collection/:id` and `/saved_search/:id` resolve here — broader than
  // Cantemo's own regex, which only ever matches `/collection/`. Moved here unchanged from
  // `CollectionBrowser/platform.ts`'s old hardcoded copy (Monday S2.6).
  getCollectionId: () =>
    window.location.pathname.match(
      /\/(?:collection|saved_search)\/([^/?]*)/
    )?.[1] ?? undefined,

  // Cantemo's version prefixes `/search/` and reads `savedsearch` (singular word) when `fullPage`
  // is set — see `HostAdapter.ts`'s own doc comment. This app has no separate full-page
  // search-results view to link to, so `fullPage` is accepted (for interface compatibility) but
  // ignored — the href shape is always `/collection/:id` or `/saved_search/:id`, unchanged from
  // `CollectionBrowserTree.tsx`'s old inline template literal (Monday S2.6, zero behavior change).
  collectionHref: (id, options) =>
    `/${options?.savedSearch ? "saved_search" : "collection"}/${id}`,

  // Preserves today's Experience demo behavior (unconditional filler entries) now that
  // `Menu.tsx` gates them behind this opt-in — no `fetchOptions`, since this app has no Portal
  // AppConfig registry either; real entries keep flowing through each `<Gear options={...}>`
  // call's own `additionalOptions`.
  getGearConfig: () => ({
    fillerEntriesEnabled: true,
    customActionsEnabled: true,
  }),

  // See this file's own `useGearMenuOptions` above. `ItemTitle.tsx` reaches its gear entries
  // through this seam now that it lives in the package (S2.2 PR D); `ResultCard.tsx` still calls
  // `useCustomActionOptions` inline, since it hasn't been lifted yet.
  useGearMenuOptions,

  // Delete/move/copy/rescan/restore stay under the explicit safety hold (docs/decisions.md,
  // 2026-09-01) until Addison lifts it — `false` keeps `@cinesys/experience-ui`'s own
  // `assertDestructiveOpAllowed` (`ItemBrowser/platform.ts`) throwing.
  destructiveOpsEnabled: () => false,

  // `item-browser/shared/components/ColorProvider.tsx` — a `styled(ColorProvider)` carrying this
  // app's `cinesysExperienceColors`, the exact component `ItemCard.tsx`/`AudioPreview.tsx` already
  // wrap their `document.body` portals in. Returned as the module-level component it is, never an
  // inline wrapper: a fresh identity per call would remount the portaled subtree every render
  // (see the member's own doc comment).
  getColorProvider: () => ColorProvider,

  // No iconik-backed vocabulary source for `tags`-type fields (Portal's
  // `/API/v2/metadata-schema/fields/:id/tags/` has no equivalent), so a tags FILTER would render a
  // permanently-empty dropdown — `false` is the same answer `item-browser/shared/utils.ts`'s own
  // `tagsFilterEnabled` gives today, not a new restriction. The tags editor still renders either
  // way, but `false` also tells `fetchTagOptions` every failure here is the expected 404, not a
  // real error to propagate (see `HostAdapter.tagsFilterEnabled`'s own doc comment).
  tagsFilterEnabled: () => false,

  // Delegates to `item-browser/shared/utils.ts`'s own `pluginEnabled` — one answer, not a second
  // copy of that switch. `JobView.tsx` (now in `@cinesys/experience-ui`) was the first caller
  // reaching this through the adapter; `DrillInPage.tsx`/`SearchPage.tsx`/`SectionPage.tsx`/
  // `Sidebar.tsx`/`Wrapper.tsx`/`ItemBrowserList.tsx` were repointed onto it too (Monday S2.5a) —
  // every remaining direct `pluginEnabled(...)` importer of `shared/utils.ts` has moved onto this
  // seam now.
  pluginEnabled,
};
